/**
 * Downloads audible media when an editor opens and holds shared blob leases for
 * instant playback. Waveform decoding uses the same cache, so this does not issue
 * a second request for a source that is already drawing its waveform.
 */

import { useRef, useState, useEffect, useCallback } from 'react'
import {
  acquireMediaBlob,
  type MediaBlobLease,
} from '../../../../api/mediaBlobCache'
import { getMediaElementUrl } from '../../../../api/mediaFetch'
import createDebug from 'debug'

const debug = createDebug('dw:timeline-playback:preload')

export interface UseClipPreloaderReturn {
  getBlobUrl: (sourcePath: string) => string | null
  /** True after every requested path has either loaded or failed. */
  isReady: boolean
  /** True when at least one requested source is playable and none remain pending. */
  readyToPlay: boolean
  loadingCount: number
  loadedCount: number
  revision: number
  errorPaths: string[]
  /** Clear failed paths and retry them through the shared cache. */
  retry: () => void
}

interface BlobEntry {
  blobUrl: string
  resolvedUrl: string
  lease: MediaBlobLease
}

interface InFlightEntry {
  resolvedUrl: string
  lease: MediaBlobLease
}

interface UseClipPreloaderProps {
  projectId: string | undefined
  sourcePaths: string[]
  /** Preserve the caller's cache-bust revision so waveform and playback share identity. */
  resolveSourceUrl?: (sourcePath: string) => string | null
}

const MAX_CONCURRENCY = 3

export function useClipPreloader({
  projectId,
  sourcePaths,
  resolveSourceUrl,
}: UseClipPreloaderProps): UseClipPreloaderReturn {
  const blobMapRef = useRef<Map<string, BlobEntry>>(new Map())
  const inFlightRef = useRef<Map<string, InFlightEntry>>(new Map())
  const errorPathsRef = useRef<Map<string, string | null>>(new Map())
  const currentRequestsRef = useRef<Map<string, string>>(new Map())
  const [revision, setRevision] = useState(0)
  const [retryRevision, setRetryRevision] = useState(0)
  const [loadingCount, setLoadingCount] = useState(0)

  const forceUpdate = useCallback(() => setRevision((current) => current + 1), [])
  const retry = useCallback(() => {
    if (errorPathsRef.current.size === 0) return
    errorPathsRef.current.clear()
    setRetryRevision((current) => current + 1)
    forceUpdate()
  }, [forceUpdate])

  // Resolve the authoritative URL during render. Besides making the effect
  // insensitive to callback identity churn, this lets a cache-bust revision
  // replace a stale lease even when the logical source path is unchanged.
  const currentRequests = new Map<string, string>()
  const resolvedEntries: Array<[string, string | null]> = []
  if (projectId) {
    for (const path of sourcePaths) {
      const resolvedUrl = resolveSourceUrl
        ? resolveSourceUrl(path)
        : getMediaElementUrl(projectId, path)
      resolvedEntries.push([path, resolvedUrl])
      if (resolvedUrl) currentRequests.set(path, resolvedUrl)
    }
  } else {
    for (const path of sourcePaths) resolvedEntries.push([path, null])
  }
  const requestSignature = JSON.stringify(resolvedEntries)
  const getBlobUrl = useCallback(
    (sourcePath: string): string | null => {
      const entry = blobMapRef.current.get(sourcePath)
      return entry && currentRequests.get(sourcePath) === entry.resolvedUrl
        ? entry.blobUrl
        : null
    },
    // The signature is the stable identity of the render-local request map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestSignature],
  )

  useEffect(() => {
    currentRequestsRef.current = currentRequests
    const requested = currentRequests
    const currentPaths = new Set(sourcePaths)
    const blobMap = blobMapRef.current
    const inFlight = inFlightRef.current

    for (const [path, entry] of blobMap) {
      if (!currentPaths.has(path) || requested.get(path) !== entry.resolvedUrl) {
        entry.lease.release()
        blobMap.delete(path)
      }
    }
    for (const [path, entry] of inFlight) {
      if (!currentPaths.has(path) || requested.get(path) !== entry.resolvedUrl) {
        entry.lease.release()
        inFlight.delete(path)
      }
    }
    for (const [path, failedUrl] of errorPathsRef.current) {
      if (!currentPaths.has(path) || (requested.get(path) ?? null) !== failedUrl) {
        errorPathsRef.current.delete(path)
      }
    }

    if (!projectId) {
      setLoadingCount(0)
      return
    }

    const needsDownload = sourcePaths.filter(
      (path) => requested.has(path) && !blobMap.has(path) && !inFlight.has(path) && !errorPathsRef.current.has(path),
    )
    for (const path of sourcePaths) {
      if (!requested.has(path)) errorPathsRef.current.set(path, null)
    }
    if (needsDownload.length === 0) {
      setLoadingCount(inFlight.size)
      return
    }

    let queueIndex = 0
    let activeCount = inFlight.size
    let cancelled = false

    const processNext = () => {
      while (!cancelled && activeCount < MAX_CONCURRENCY && queueIndex < needsDownload.length) {
        const path = needsDownload[queueIndex++]
        if (blobMap.has(path) || inFlight.has(path)) continue
        const resolvedUrl = requested.get(path)
        if (!resolvedUrl) {
          errorPathsRef.current.set(path, null)
          forceUpdate()
          continue
        }

        const lease = acquireMediaBlob({ projectId, sourcePath: path, resolvedUrl })
        inFlight.set(path, { resolvedUrl, lease })
        activeCount += 1
        setLoadingCount(inFlight.size)
        debug('acquiring shared media: %s', path)

        lease.promise
          .then(({ blobUrl }) => {
            if (inFlight.get(path)?.lease !== lease) return
            inFlight.delete(path)
            activeCount -= 1
            if (currentRequestsRef.current.get(path) !== resolvedUrl) {
              lease.release()
              return
            }
            blobMap.set(path, { blobUrl, resolvedUrl, lease })
            setLoadingCount(inFlight.size)
            forceUpdate()
            if (!cancelled) processNext()
          })
          .catch((error) => {
            if (inFlight.get(path)?.lease !== lease) return
            inFlight.delete(path)
            activeCount -= 1
            lease.release()
            if (currentRequestsRef.current.get(path) !== resolvedUrl) return
            if (error instanceof DOMException && error.name === 'AbortError') return
            console.error(`Failed to preload clip: ${path}`, error)
            errorPathsRef.current.set(path, resolvedUrl)
            setLoadingCount(inFlight.size)
            forceUpdate()
            if (!cancelled) processNext()
          })
      }
    }

    processNext()
    return () => {
      cancelled = true
    }
    // sourcePaths and its resolved URLs are represented by requestSignature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, requestSignature, retryRevision, revision, forceUpdate])

  useEffect(() => {
    const blobMap = blobMapRef.current
    const inFlight = inFlightRef.current
    return () => {
      for (const entry of blobMap.values()) entry.lease.release()
      blobMap.clear()
      for (const entry of inFlight.values()) entry.lease.release()
      inFlight.clear()
    }
  }, [])

  const hasFreshBlob = (path: string): boolean => {
    const entry = blobMapRef.current.get(path)
    return Boolean(entry && currentRequests.get(path) === entry.resolvedUrl)
  }
  const hasFreshError = (path: string): boolean =>
    errorPathsRef.current.has(path) &&
    errorPathsRef.current.get(path) === (currentRequests.get(path) ?? null)
  const loadedCount = sourcePaths.filter(hasFreshBlob).length
  const errorPaths = sourcePaths.filter(hasFreshError)
  const isReady =
    loadingCount === 0 &&
    sourcePaths.every((path) => hasFreshBlob(path) || hasFreshError(path))
  const readyToPlay = isReady && loadedCount > 0

  return {
    getBlobUrl,
    isReady,
    readyToPlay,
    loadingCount,
    loadedCount,
    revision,
    errorPaths,
    retry,
  }
}
